import React, { useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { ArrowLeft, Clock, Eye, Share2 } from 'lucide-react'
import PostBody from '../components/PostBody'
import { Button } from '../components/ui/button'
import { Badge } from '../components/ui/badge'
import { Post, Category } from '../types'
import { api } from '../services/api'

const SITE_URL = 'https://excelnerds.com'

function truncateDescription(text: string, max = 160): string {
  if (!text) return ''
  const clean = text.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim()
  if (clean.length <= max) return clean
  return clean.substring(0, max).replace(/\s+\S*$/, '') + '...'
}

const PostPage: React.FC = () => {
  const { categorySlug, postSlug } = useParams<{ 
    categorySlug: string
    postSlug: string 
  }>()
  const navigate = useNavigate()
  const [post, setPost] = useState<Post | null>(null)
  const [category, setCategory] = useState<Category | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchPostData = async () => {
      if (!categorySlug || !postSlug) return

      try {
        setLoading(true)
        setError(null)

        const [postRes, categoryRes] = await Promise.all([
          api.get(`/posts/${postSlug}`),
          api.get(`/categories/${categorySlug}`)
        ])

        setPost(postRes.data || null)
        setCategory(categoryRes.data || null)

        // Increment view count
        await api.patch(`/posts/${postRes.data.id}/view`)
      } catch (err: any) {
        console.error('Error fetching post data:', err)
        setError(err.response?.data?.error || 'Failed to load post')
      } finally {
        setLoading(false)
      }
    }

    fetchPostData()
  }, [categorySlug, postSlug])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-excel-green mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading article...</p>
        </div>
      </div>
    )
  }

  if (error || !post || !category) {
    // Automatically redirect to home page when article not found
    useEffect(() => {
      const timer = setTimeout(() => {
        navigate('/', { replace: true })
      }, 100)
      return () => clearTimeout(timer)
    }, [navigate])

    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Redirecting to home page...</p>
        </div>
      </div>
    )
  }

  const categoryColorMap: Record<string, string> = {
    'excel-tutorials': 'excel',
    'tips-tricks': 'amber',
    'news-updates': 'blue',
    'advanced-techniques': 'purple',
    'business-solutions': 'teal'
  }

  const categoryColor = categoryColorMap[category.slug] || 'default'

  const pageTitle = `${post.title} | Excel Nerds`
  const metaDescription = truncateDescription(post.excerpt || post.meta_description || post.title)
  const canonicalUrl = `${SITE_URL}/${category.slug}/${post.slug}`
  const imageUrl = post.featured_image_url || ''
  const datePublished = post.created_at || new Date().toISOString()
  const dateModified = post.updated_at || datePublished

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: metaDescription,
    ...(imageUrl && { image: imageUrl }),
    author: { '@type': 'Person', name: post.author || 'Excel Nerds' },
    publisher: {
      '@type': 'Organization',
      name: 'Excel Nerds',
      logo: { '@type': 'ImageObject', url: `${SITE_URL}/static/logo.png` }
    },
    datePublished,
    dateModified,
    mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl }
  }

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        {imageUrl && <meta property="og:image" content={imageUrl} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={metaDescription} />
        {imageUrl && <meta name="twitter:image" content={imageUrl} />}
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      {/* Header */}
      <div className="border-b bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center mb-6">
              <Link to={`/${categorySlug}`}>
                <Button variant="ghost" className="mr-4">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to {category.name}
                </Button>
              </Link>
            </div>

            <Badge variant={categoryColor as any} className="mb-4">
              {category.name}
            </Badge>

            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              {post.title}
            </h1>

            {post.excerpt && (
              <p className="text-xl text-gray-600 mb-6 leading-relaxed">
                {post.excerpt}
              </p>
            )}

            <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500">
              <div className="flex items-center">
                <Clock className="h-4 w-4 mr-2" />
                {Math.ceil(post.content.length / 1000)} min read
              </div>
              <div className="flex items-center">
                <Eye className="h-4 w-4 mr-2" />
                {post.view_count} views
              </div>
              <Button variant="ghost" size="sm" className="ml-auto">
                <Share2 className="h-4 w-4 mr-2" />
                Share
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <article className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {post.featured_image_url && (
            <div className="mb-8">
              <img 
                src={post.featured_image_url} 
                alt={post.title}
                className="w-full h-auto rounded-lg shadow-sm"
              />
            </div>
          )}

          <PostBody markdown={post.content.replace(/^#\s+.*?\n/, '')} />

          {/* Footer */}
          <div className="mt-16 pt-8 border-t">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div className="mb-4 md:mb-0">
                <p className="text-gray-600">
                  Found this helpful? Share it with others!
                </p>
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    const shareUrl = encodeURIComponent(window.location.href)
                    const shareText = encodeURIComponent(post.title)
                    window.open(`https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareText}`, '_blank')
                  }}
                >
                  Share on X
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    const shareUrl = encodeURIComponent(window.location.href)
                    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`, '_blank')
                  }}
                >
                  Share on LinkedIn
                </Button>
              </div>
            </div>
          </div>
        </div>
      </article>

      {/* Related Posts */}
      <section className="bg-gray-50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">
              More from {category.name}
            </h2>
            <div className="text-center py-8">
              <p className="text-gray-600 mb-4">
                Related articles coming soon...
              </p>
              <Link to={`/${categorySlug}`}>
                <Button variant="excel">
                  Browse All {category.name}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default PostPage
